import { WHALE_PORTRAIT_URL } from '../../../shared/animalArtwork';

import { stableHash } from './stableHash';

const GRID_SIZE = 3;
const TILE_COUNT = GRID_SIZE * GRID_SIZE;

export const LAUNCH_ACTIVITY_SPECIES = [
  'Emperor penguin',
  'Green sea turtle',
  'African elephant',
  'Humpback whale',
] as const;

export type LaunchActivitySpecies = (typeof LAUNCH_ACTIVITY_SPECIES)[number];

interface AnimalActivityPack {
  artUrl: string;
  puzzleScene: string;
  puzzleAspectRatio?: string;
  puzzleCompletion: string;
  offlineActivities: readonly OfflineActivity[];
}

export interface OfflineActivity {
  title: string;
  prompt: string;
  science: string;
}

const ACTIVITY_PACKS: Record<LaunchActivitySpecies, AnimalActivityPack> = {
  'Emperor penguin': {
    artUrl: '/art/percy.svg',
    puzzleScene: 'on the ice',
    puzzleCompletion: 'an icy adventure',
    offlineActivities: [
      {
        title: 'Cozy-place detective',
        prompt:
          'With a grown-up, look for one place nearby that would feel sheltered from wind or rain. It could be under a porch, beside a hedge, or behind a big rock.',
        science:
          'Emperor penguins huddle together to stay warm. Watch wildlife from a distance, and leave animals and their homes alone.',
      },
      {
        title: 'Huddle heat test',
        prompt:
          'Stand shoulder-to-shoulder with your grown-up, then take one step apart. Which position feels more sheltered from moving air?',
        science:
          'Emperor penguins take turns moving through a huddle so many birds can share the warmer center.',
      },
      {
        title: 'Flipper-motion mime',
        prompt:
          'Pretend your arms are penguin flippers. Move them slowly as if you are steering through water, while keeping both feet safely on the ground.',
        science: 'An emperor penguin’s flipper-shaped wings push and steer its body underwater.',
      },
      {
        title: 'Ice-grip comparison',
        prompt:
          'With a grown-up, compare the tread on two safe pairs of shoes. Which tread might grip a slippery surface better, and why?',
        science: 'Strong claws help emperor penguins grip Antarctic sea ice.',
      },
      {
        title: 'Call-and-find game',
        prompt:
          'Choose two different gentle sounds with your grown-up. Close your eyes in a safe room and point toward the sound you hear.',
        science:
          'Distinctive calls help emperor penguins recognize one another in a crowded colony.',
      },
      {
        title: 'Warm-layer search',
        prompt:
          'Find three clothing layers used on a cold day. Put them in the order you would wear them, without going outdoors in unsafe weather.',
        science: 'Several feather layers and body fat insulate emperor penguins from extreme cold.',
      },
      {
        title: 'Food-web chain',
        prompt:
          'Use four household objects to stand for ocean plants, krill, fish, and a penguin. Arrange them in a food chain.',
        science:
          'Emperor penguins eat fish, krill, and squid near the top of the Southern Ocean food web.',
      },
      {
        title: 'Long-dive counting',
        prompt:
          'Take one normal breath, then count slowly to five while breathing normally. Imagine how much practice a diving bird needs underwater.',
        science:
          'Most emperor penguin dives last several minutes, and the longest recorded dive lasted 22 minutes.',
      },
    ],
  },
  'Green sea turtle': {
    artUrl: '/art/shelly.svg',
    puzzleScene: 'gliding over the reef',
    puzzleCompletion: 'a reef adventure',
    offlineActivities: [
      {
        title: 'Pattern-and-texture scout',
        prompt:
          'With a grown-up, find three different patterns or textures outdoors. Look closely, describe each one, and leave everything where you found it.',
        science:
          'A green sea turtle has a patterned shell and uses strong front flippers to swim. Watch wildlife from a distance, and leave animals and their homes alone.',
      },
      {
        title: 'Flipper-stroke mime',
        prompt:
          'Stand in a clear space and sweep your arms slowly like long front flippers. Try a wide stroke and a narrow stroke.',
        science: 'Green turtles use long front flippers to move through ocean water.',
      },
      {
        title: 'Beach-horizon finder',
        prompt:
          'From a safe place with a grown-up, compare the bright open sky with a darker wall or tree line. Point toward the brighter horizon.',
        science:
          'On natural beaches, green turtle hatchlings move toward the brighter ocean horizon.',
      },
      {
        title: 'Seagrass menu maker',
        prompt:
          'Draw or arrange a pretend underwater menu with three leafy foods. Do not collect real plants or feed wildlife.',
        science: 'Adult green turtles mainly graze on seagrasses and algae.',
      },
      {
        title: 'Migration stepping path',
        prompt:
          'Place five safe objects in a line and walk beside them as a pretend route from a feeding ground to a nesting beach.',
        science:
          'Green turtles can migrate hundreds or thousands of miles between feeding and nesting areas.',
      },
      {
        title: 'Shell-shape sketch',
        prompt:
          'Sketch an oval shell and add a center row of five pretend scutes, then four along each side.',
        science: 'A green turtle shell has five central scutes and four scutes on each side.',
      },
      {
        title: 'Debris safety sort',
        prompt:
          'With a grown-up, sort clean household items into reusable, recyclable, and trash groups. Keep every item away from wildlife.',
        science: 'Marine debris can injure green turtles or be mistaken for food.',
      },
      {
        title: 'Surface-for-air pause',
        prompt:
          'Move slowly around a clear room, pausing at a chosen spot each lap to take one calm breath.',
        science: 'Green turtles are reptiles that must reach the surface to breathe air.',
      },
    ],
  },
  'African elephant': {
    artUrl: '/art/ellie.svg',
    puzzleScene: 'crossing the grassland',
    puzzleCompletion: 'a grassland adventure',
    offlineActivities: [
      {
        title: 'Big-ears listening walk',
        prompt:
          'With a grown-up, stand or sit somewhere safe and quiet. Listen for one nearby sound and one faraway sound, then describe how they are different.',
        science:
          'African elephants listen to many sounds and communicate with deep rumbles. Watch wildlife from a distance, and leave animals and their homes alone.',
      },
      {
        title: 'Trunk-tool charades',
        prompt:
          'Use one arm as a pretend trunk and mime smelling, drinking, touching, and gently lifting a light scarf.',
        science: 'An elephant uses its trunk to smell, breathe, drink, touch, and grasp.',
      },
      {
        title: 'Cooling-fan test',
        prompt:
          'Fan your face gently with a piece of paper, then stop. Describe how moving air changes what you feel.',
        science: 'Large ears help an African savanna elephant release body heat.',
      },
      {
        title: 'Cushioned-step walk',
        prompt:
          'Walk softly across a safe room, then compare a heel-first step with a quiet whole-foot step.',
        science: 'Thick pads in an elephant’s feet cushion its steps.',
      },
      {
        title: 'Herd-leader route',
        prompt:
          'Take turns leading a grown-up through a short, clear route around safe household objects.',
        science: 'An experienced female often helps guide an elephant family.',
      },
      {
        title: 'Plant-menu search',
        prompt:
          'Find pictures of three plant foods, such as grass, leaves, fruit, roots, or bark. Leave outdoor plants where they grow.',
        science: 'African savanna elephants eat many kinds of plant material.',
      },
      {
        title: 'Water-stop planner',
        prompt:
          'Draw a simple route with two pretend water stops and one shady rest stop. Explain why each stop matters.',
        science: 'Elephants travel to find water for drinking, cooling, and bathing.',
      },
      {
        title: 'Low-rumble experiment',
        prompt:
          'Hum gently at a comfortable low pitch, then at a comfortable higher pitch. Notice where each vibration feels strongest.',
        science: 'Some elephant rumbles are lower than people can easily hear and can travel far.',
      },
    ],
  },
  'Humpback whale': {
    artUrl: WHALE_PORTRAIT_URL,
    puzzleAspectRatio: '1450 / 1085',
    puzzleScene: 'swimming through the open ocean',
    puzzleCompletion: 'an ocean adventure',
    offlineActivities: [
      {
        title: 'Migration-route maker',
        prompt:
          'With a grown-up, place three safe household objects on a table or floor as ocean stops. Trace a long route between them with your finger, then try a new route.',
        science:
          'Some humpback whales travel long distances between feeding and breeding areas. Watch wildlife from a distance, and never approach or feed wild animals.',
      },
      {
        title: 'Bubble-net circle',
        prompt:
          'Arrange small paper circles around three blocks, then make the circle smaller without touching the blocks.',
        science:
          'Some humpbacks use curtains of bubbles to gather schooling prey into a smaller space.',
      },
      {
        title: 'Baleen-sieve model',
        prompt:
          'With a grown-up, use a clean kitchen strainer and dry pasta to model how a sieve holds food while smaller pieces pass through.',
        science:
          'Baleen plates act like a sieve when humpbacks filter krill and small fish from water.',
      },
      {
        title: 'Flipper-span measure',
        prompt:
          'Stretch both arms in a clear space and ask a grown-up to measure your fingertip-to-fingertip span.',
        science:
          'The name Megaptera means big-winged and refers to a humpback’s long pectoral fins.',
      },
      {
        title: 'Pattern-song builder',
        prompt:
          'Create a quiet three-sound pattern by clapping, tapping, and humming, then repeat it in the same order.',
        science: 'Humpback songs are long, patterned sequences of sound.',
      },
      {
        title: 'Tail-pattern designer',
        prompt:
          'Draw two tail shapes and give each a different edge and color pattern. Explain how you would tell them apart.',
        science:
          'Researchers use the unique shapes and markings of tail flukes to recognize individual humpbacks.',
      },
      {
        title: 'Blubber-layer model',
        prompt:
          'Wrap a room-temperature cup in a towel and leave another unwrapped. Ask a grown-up how the layer changes heat loss.',
        science: 'Humpbacks build blubber that stores energy and insulates their bodies.',
      },
      {
        title: 'Safe-viewing distance',
        prompt:
          'Use a hallway or outdoor path to imagine a long wildlife-viewing distance. Practice observing without moving closer.',
        science: 'Responsible viewers stay at least 100 yards away from large whales.',
      },
    ],
  },
};

export function isLaunchActivitySpecies(
  speciesName?: string,
): speciesName is LaunchActivitySpecies {
  return LAUNCH_ACTIVITY_SPECIES.includes(speciesName as LaunchActivitySpecies);
}

export function getAnimalActivityPack(speciesName?: string): AnimalActivityPack | null {
  return isLaunchActivitySpecies(speciesName) ? ACTIVITY_PACKS[speciesName] : null;
}

export function getLaunchAnimalActivityPack(
  speciesName: LaunchActivitySpecies,
): AnimalActivityPack {
  return ACTIVITY_PACKS[speciesName];
}

export function getDailyOfflineActivity(
  speciesName: LaunchActivitySpecies,
  calendarDate: string,
): OfflineActivity {
  const activities = ACTIVITY_PACKS[speciesName].offlineActivities;
  return activities[
    stableHash(`${speciesName}|offline-activity|${calendarDate}`) % activities.length
  ]!;
}

export function createPuzzleTiles(
  speciesName: LaunchActivitySpecies,
  calendarDate: string,
): number[] {
  const tiles = Array.from({ length: TILE_COUNT }, (_, index) => index);
  let seed = stableHash(`${speciesName}|picture-puzzle|${calendarDate}`);

  for (let index = tiles.length - 1; index > 0; index -= 1) {
    seed = (seed * 1_664_525 + 1_013_904_223) >>> 0;
    const swapIndex = seed % (index + 1);
    [tiles[index], tiles[swapIndex]] = [tiles[swapIndex]!, tiles[index]!];
  }

  if (tiles.every((tile, index) => tile === index)) {
    [tiles[0], tiles[1]] = [tiles[1]!, tiles[0]!];
  }
  return tiles;
}

export function isPuzzleSolved(tiles: number[]): boolean {
  return tiles.every((tile, index) => tile === index);
}

export function swapPuzzleTiles(
  tiles: number[],
  firstIndex: number,
  secondIndex: number,
): number[] {
  const nextTiles = [...tiles];
  [nextTiles[firstIndex], nextTiles[secondIndex]] = [
    nextTiles[secondIndex]!,
    nextTiles[firstIndex]!,
  ];
  return nextTiles;
}
